// Shared inner body for the Oscar account wish-list detail page (the line
// formset with per-line quantity + remove/move actions). Renders inside
// AccountPageBody (the account side-nav shell) without the document chrome --
// each site wraps this in its own PageSkeleton so the DOM is byte-identical to
// the pre-move AccountPageShell.
import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";

import { CSRFToken } from "@reactivated/forms";

import { FieldControl } from "../../../forms/FormPageFields";
import { AccountPageBody } from "../AccountPageBody";

type ILine = Immutable<
    templates.WishListDetailDocumentTemplate["lines"][number]
>;

const LineQuantityFields = (
    props: Immutable<{ line: ILine }>,
): React.JSX.Element => (
    <>
        {props.line.fields.map((field) => {
            if (field.kind === "hidden") {
                return <FieldControl key={field.field_id} field={field} />;
            }
            // Stock wishlists_detail.html renders the quantity field via
            // widget_tweaks `class+="form-control"`, appended to the widget's
            // own `input-mini` (set in WishListLineForm.__init__).
            return (
                <React.Fragment key={field.field_id}>
                    <input
                        type={field.input_type || "text"}
                        className="input-mini form-control"
                        id={field.field_id}
                        name={field.name}
                        required={field.required || undefined}
                        defaultValue={field.value}
                    />
                </React.Fragment>
            );
        })}
        {props.line.field_errors.length > 0 &&
            props.line.field_errors.map((error, i) => (
                <ul className="error-block" key={i}>
                    <li>{error}</li>
                </ul>
            ))}
    </>
);

type IProps = Immutable<templates.WishListDetailDocumentTemplate>;

export const WishListDetailBody = (props: IProps): React.JSX.Element => {
    const {
        has_lines,
        action_url,
        management_fields,
        lines,
        empty_message,
        product_header_label,
        quantity_label,
        action_label,
        view_product_label,
        actions_label,
        remove_label,
        not_available_label,
        update_label,
        updating_label,
    } = props;
    return (
        <AccountPageBody sideNav={props.side_nav}>
            {has_lines ? (
                <form method="post" action={action_url}>
                    {management_fields.map((field) => (
                        <FieldControl key={field.field_id} field={field} />
                    ))}
                    <CSRFToken />
                    <table className="table table-striped table-bordered">
                        <tbody>
                            <tr>
                                <th colSpan={2}>{product_header_label}</th>
                                <th>{quantity_label}</th>
                                <th>{action_label}</th>
                            </tr>

                            {lines.map((line, i) => (
                                <tr key={i}>
                                    <td>
                                        {line.image_url &&
                                            (line.is_public ? (
                                                <a href={line.product_url}>
                                                    <img
                                                        className="img-thumbnail"
                                                        src={line.image_url}
                                                        alt={line.title}
                                                        loading="lazy"
                                                        decoding="async"
                                                    />
                                                </a>
                                            ) : (
                                                <img
                                                    className="img-thumbnail"
                                                    src={line.image_url}
                                                    alt={line.title}
                                                    loading="lazy"
                                                    decoding="async"
                                                />
                                            ))}
                                    </td>
                                    <td>
                                        {line.is_public ? (
                                            <a href={line.product_url}>
                                                {line.title}
                                            </a>
                                        ) : (
                                            <em>
                                                {line.title} (
                                                {not_available_label})
                                            </em>
                                        )}
                                    </td>
                                    <td>
                                        <LineQuantityFields line={line} />
                                    </td>
                                    <td>
                                        <div className="btn-group">
                                            {line.has_product ? (
                                                <>
                                                    <a
                                                        className="btn btn-secondary"
                                                        href={line.product_url}
                                                    >
                                                        {view_product_label}
                                                    </a>
                                                    <button
                                                        className="btn btn-secondary dropdown-toggle"
                                                        data-toggle="dropdown"
                                                    ></button>
                                                </>
                                            ) : (
                                                <a
                                                    className="btn btn-secondary dropdown-toggle"
                                                    data-toggle="dropdown"
                                                    href="#"
                                                >
                                                    {actions_label}
                                                </a>
                                            )}
                                            <ul className="dropdown-menu dropdown-menu-right">
                                                <li>
                                                    <a
                                                        href={line.remove_url}
                                                        className="dropdown-item"
                                                    >
                                                        {remove_label}
                                                    </a>
                                                </li>
                                                {line.move_targets.map(
                                                    (target, j) => (
                                                        <li key={j}>
                                                            <a
                                                                href={
                                                                    target.url
                                                                }
                                                                className="dropdown-item"
                                                            >
                                                                {target.label}
                                                            </a>
                                                        </li>
                                                    ),
                                                )}
                                            </ul>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            <tr>
                                <td colSpan={2}></td>
                                <td>
                                    <button
                                        className="btn btn-primary"
                                        type="submit"
                                        data-loading-text={updating_label}
                                    >
                                        {update_label}
                                    </button>
                                </td>
                                <td></td>
                            </tr>
                        </tbody>
                    </table>
                </form>
            ) : (
                empty_message
            )}
        </AccountPageBody>
    );
};
